"use client";

import {
	Button,
	FeaturedText,
	RichText,
	Tag
} from "@pamperedchef/design-system";
import type { Cta, CmsImage } from "@/composites/landing/types";
import * as styles from "./Hero.module.scss";

export interface HeroProps {
	eyebrow?: string;
	headline: string;
	subcopy?: string;
	primaryCta?: Cta;
}

/**
 * Landing-page hero — full-width, centered. An optional invite pill (success
 * `Tag` with a gift icon), the headline in the brand teal serif display
 * (`FeaturedText`), centered subcopy, and a yellow filled primary CTA. The
 * background defaults to the brand mist wash; an optional image sits behind.
 * Presentational, props-in, RSC-safe (CTA is an `href`, no handlers).
 */
export function Hero({ eyebrow, headline, subcopy, primaryCta }: HeroProps) {
	return (
		<section className={styles["hero"]} data-component="Hero">
			<div className={styles["hero__content"]}>
				{eyebrow ? (
					<Tag
						kind="subtle"
						color="lime-green"
						size="sm"
						label={eyebrow}
						icon={{ name: "gift" }}
						className={styles["hero__pill"]}
					/>
				) : null}

				<FeaturedText
					className={styles["hero__headline"]}
					headline={headline}
					styledAs="display-lg"
					alignment="center"
					headingTag="h1"
				/>

				{subcopy ? (
					<RichText
						content={subcopy}
						className={styles["hero__subcopy"]}
					/>
				) : null}

				{primaryCta ? (
					<Button
						kind="primary"
						size="lg"
						onPress={() => {
							window.location.href = primaryCta.href;
						}}
						className={styles["hero__cta"]}
					>
						{primaryCta.title}
					</Button>
				) : null}
			</div>
		</section>
	);
}
